import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { GoalTypes } from '../../components/layout/treatment-plan/goals/old/constants';
import { API_ENDPOINT } from '../../constants';
import { fetchWrapper } from '../../helpers';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type {
  ITreatmentPlanGoal,
  ITreatmentPlanGoalTarget,
  ITrialSummary,
} from '../../utils/types';

const name = 'goals';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<GoalsState>) => createExtraReducers(builder),
});

type OriginalGoals = Partial<Record<GoalTypes, Record<number, ITreatmentPlanGoal>>>;

export interface GoalsState {
  loading: boolean;
  error: any;
  list: Array<ITreatmentPlanGoal>;
  behaviorReductionGoals: Array<ITreatmentPlanGoal>;
  skillAcquisitionGoals: Array<ITreatmentPlanGoal>;
  caregiverGoals: Array<ITreatmentPlanGoal>;
  validationErrors: any;
  treatmentPlanId: number | null;
  treatmentPlanStatusId: number | null;
  originalGoals: OriginalGoals | null; // Persist original goals before editing to revert back on cancel editing.
  tpReportGoalsList: Array<ITreatmentPlanGoal>; // New state variable for tp_report_goals
  tpGoalsTrialSummary: { [key: string]: ITrialSummary | null }; // State to store target trial summary of each goal for charts
}

function createInitialState(): GoalsState {
  return {
    list: [],
    behaviorReductionGoals: [],
    skillAcquisitionGoals: [],
    caregiverGoals: [],
    loading: false,
    error: null,
    validationErrors: null,
    treatmentPlanId: null,
    treatmentPlanStatusId: null,
    originalGoals: null,
    tpReportGoalsList: [],
    tpGoalsTrialSummary: {},
  };
}

function createReducers() {
  return {
    clearState: clearState,
    updateGoals: updateGoals,
    updateState: updateState,
    updateGoalProperty: updateGoalProperty,
    updateOriginalGoal: updateOriginalGoal,
    persistOriginalGoal: persistOriginalGoal,
    updateGoalsExpandState: updateGoalsExpandState,
  };

  function clearState(state: GoalsState) {
    state.list = [];
    state.behaviorReductionGoals = [];
    state.skillAcquisitionGoals = [];
    state.caregiverGoals = [];
    state.validationErrors = null;
    state.tpReportGoalsList = [];
    state.tpGoalsTrialSummary = {};
  }

  function updateGoalsExpandState(state: GoalsState, action: any) {
    const { expandState } = action.payload;
    state.behaviorReductionGoals = state.behaviorReductionGoals.map((goal) => ({
      ...goal,
      expanded: expandState,
    }));
    state.caregiverGoals = state.caregiverGoals.map((goal) => ({
      ...goal,
      expanded: expandState,
    }));
    state.skillAcquisitionGoals = state.skillAcquisitionGoals.map((goal) => ({
      ...goal,
      expanded: expandState,
    }));
  }
  function updateGoalProperty(state: GoalsState, action: any) {
    const { goalIndex, goalType, key, value } = action.payload;
    if (!Number.isInteger(goalIndex) || !goalType || !key) {
      return;
    }
    switch (goalType) {
      case GoalTypes.BehaviorReduction:
        state.behaviorReductionGoals = state.behaviorReductionGoals.map((goal, index) => {
          if (index === goalIndex) {
            return {
              ...goal,
              [key]: value,
            };
          }
          return goal;
        });
        break;
      case GoalTypes.Caregiver:
        state.caregiverGoals = state.caregiverGoals.map((goal, index) => {
          if (index === goalIndex) {
            return {
              ...goal,
              [key]: value,
            };
          }
          return goal;
        });
        break;
      case GoalTypes.SkillAcquisition:
        state.skillAcquisitionGoals = state.skillAcquisitionGoals.map((goal, index) => {
          if (index === goalIndex) {
            return {
              ...goal,
              [key]: value,
            };
          }
          return goal;
        });
        break;
    }
  }

  function updateGoals(state: GoalsState, action: any) {
    const { type, list } = action.payload;
    switch (type) {
      case GoalTypes.BehaviorReduction:
        state.behaviorReductionGoals = list;
        break;
      case GoalTypes.SkillAcquisition:
        state.skillAcquisitionGoals = list;
        break;
      case GoalTypes.Caregiver:
        state.caregiverGoals = list;
        break;
      default:
        break;
    }
  }

  function updateState(state: GoalsState, action: any) {
    const { key, value } = action.payload;
    Object.assign(state, { [key]: value });
  }

  // This action is only needed in rare cases.
  // Eg. When a user migrates a V1 baseline_measurement value to V2,
  // This will update DB even without saving goal.
  // To handle such kind of scenarios, we will have to update originalGoals
  function updateOriginalGoal(
    state: GoalsState,
    action: PayloadAction<{ goalType: GoalTypes; goalIndex: number; goal: ITreatmentPlanGoal }>,
  ) {
    if (!state.originalGoals) return;
    const { goalType, goalIndex, goal } = action.payload;

    const persistedGoal = state.originalGoals?.[goalType]?.[goalIndex];
    if (persistedGoal) {
      state.originalGoals = {
        ...state.originalGoals,
        [goalType]: {
          ...state.originalGoals?.[goalType],
          [goalIndex]: goal,
        },
      };
    }
  }

  // Persist original goals before editing to revert back on cancel editing.
  function persistOriginalGoal(
    state: GoalsState,
    action: PayloadAction<{ goalType: GoalTypes; index: number; goal: ITreatmentPlanGoal }>,
  ) {
    const { goalType, index, goal } = action.payload;

    state.originalGoals = {
      ...state.originalGoals,
      [goalType]: {
        ...state.originalGoals?.[goalType],
        [index]: goal,
      },
    };
  }
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/treatment_plans`;

  return {
    getGoals: getGoals(),
    createGoal: createGoal(),
    updateGoal: updateGoal(),
    removeGoal: removeGoal(),
    createGoalTarget: createGoalTarget(),
    updateGoalTarget: updateGoalTarget(),
    removeGoalTarget: removeGoalTarget(),
    activateGoal: activateGoal(),
    deactivateGoal: deactivateGoal(),
    activateTarget: activateTarget(),
    deactivateTarget: deactivateTarget(),
    changeTargetStage: changeTargetStage(),
    updateTargetStage: updateTargetStage(),
    deleteTargetStage: deleteTargetStage(),
    bulkStageChange: bulkStageChange(),
    createHypothesizedFunctionRecord: createHypothesizedFunctionRecord(),
    updateHypothesizedFunctionRecord: updateHypothesizedFunctionRecord(),
    deleteHypothesizedFunctionRecord: deleteHypothesizedFunctionRecord(),
    migrateTreatmentPlanResourceVersion: migrateTreatmentPlanResourceVersion(),
  };

  function getGoals() {
    return createAsyncThunk(
      `${name}/getGoals`,
      async ({ treatmentPlanId, date, include_pending = false, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: date
            ? `${baseUrl}/${treatmentPlanId}/goals/?date=${date}&include_pending=${include_pending}`
            : `${baseUrl}/${treatmentPlanId}/goals/?include_pending=${include_pending}`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createGoal() {
    return createAsyncThunk(
      `${name}/createGoal`,
      async ({ treatmentPlanId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${treatmentPlanId}/goals/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function updateGoal() {
    return createAsyncThunk(
      `${name}/updateGoal`,
      async ({ treatmentPlanId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${treatmentPlanId}/goals/${data.id}/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function removeGoal() {
    return createAsyncThunk(
      `${name}/removeGoal`,
      async ({ treatmentPlanId, goalId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${treatmentPlanId}/goals/${goalId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createGoalTarget() {
    return createAsyncThunk(
      `${name}/createGoalTarget`,
      async ({ treatmentPlanId, goalId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${treatmentPlanId}/goals/${goalId}/targets/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function updateGoalTarget() {
    return createAsyncThunk(
      `${name}/updateGoalTarget`,
      async ({ treatmentPlanId, goalId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${treatmentPlanId}/goals/${goalId}/targets/${data.id}/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function removeGoalTarget() {
    return createAsyncThunk(
      `${name}/removeGoalTarget`,
      async ({ treatmentPlanId, goalId, targetId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${treatmentPlanId}/goals/${goalId}/targets/${targetId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function activateGoal() {
    return createAsyncThunk(
      `${name}/activateGoal`,
      async ({ treatmentPlanId, goalId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${treatmentPlanId}/goals/${goalId}/activate/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function deactivateGoal() {
    return createAsyncThunk(
      `${name}/deactivateGoal`,
      async ({ treatmentPlanId, goalId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${treatmentPlanId}/goals/${goalId}/deactivate/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function createHypothesizedFunctionRecord() {
    return createAsyncThunk(
      `${name}/createHypothesizedFunctionRecord`,
      async ({ treatmentPlanId, goalId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${treatmentPlanId}/goals/${goalId}/hypothesized_functions/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function updateHypothesizedFunctionRecord() {
    return createAsyncThunk(
      `${name}/updateHypothesizedFunctionRecord`,
      async ({ treatmentPlanId, goalId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${treatmentPlanId}/goals/${goalId}/hypothesized_functions/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function deleteHypothesizedFunctionRecord() {
    return createAsyncThunk(
      `${name}/deleteHypothesizedFunctionRecord`,
      async ({ treatmentPlanId, goalId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${treatmentPlanId}/goals/${goalId}/hypothesized_functions/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function activateTarget() {
    return createAsyncThunk(
      `${name}/activateTarget`,
      async ({ treatmentPlanId, goalId, targetId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${treatmentPlanId}/goals/${goalId}/targets/${targetId}/activate/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function deactivateTarget() {
    return createAsyncThunk(
      `${name}/deactivateTarget`,
      async ({ treatmentPlanId, goalId, targetId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${treatmentPlanId}/goals/${goalId}/targets/${targetId}/deactivate/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function changeTargetStage() {
    return createAsyncThunk(
      `${name}/changeTargetStage`,
      async ({ treatmentPlanId, goalId, targetId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.put(
          {
            url: `${baseUrl}/${treatmentPlanId}/goals/${goalId}/targets/${targetId}/change_stage/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function migrateTreatmentPlanResourceVersion() {
    return createAsyncThunk(
      `${name}/migrateTreatmentPlanResourceVersion`,
      async ({
        treatmentPlanId,
        goalId,
        preview = false,
        goalType: _goalType,
        getAccessTokenSilently,
        data,
      }: any) => {
        let url = `${baseUrl}/${treatmentPlanId}/goals/${goalId}/migrate/`;
        if (preview) {
          url += `?preview=${preview}`;
        }
        const response = await fetchWrapper.put(
          {
            url,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function updateTargetStage() {
    return createAsyncThunk(
      `${name}/updateTargetStage`,
      async ({ treatmentPlanId, goalId, targetId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${treatmentPlanId}/goals/${goalId}/targets/${targetId}/change_stage/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function deleteTargetStage() {
    return createAsyncThunk(
      `${name}/deleteTargetStage`,
      async ({ treatmentPlanId, goalId, targetId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${treatmentPlanId}/goals/${goalId}/targets/${targetId}/change_stage/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function bulkStageChange() {
    return createAsyncThunk(
      `${name}/bulkStageChange`,
      async ({ treatmentPlanId, goalId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.put(
          {
            url: `${baseUrl}/${treatmentPlanId}/goals/${goalId}/targets/bulk_change_stage/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<GoalsState>) {
  const {
    getGoals,
    createGoal,
    updateGoal,
    removeGoal,
    createGoalTarget,
    updateGoalTarget,
    removeGoalTarget,
    activateGoal,
    deactivateGoal,
    activateTarget,
    deactivateTarget,
    changeTargetStage,
    updateTargetStage,
    deleteTargetStage,
    bulkStageChange,
    createHypothesizedFunctionRecord,
    updateHypothesizedFunctionRecord,
    deleteHypothesizedFunctionRecord,
    migrateTreatmentPlanResourceVersion,
  } = extraActions;

  builder
    // getGoals()
    .addCase(getGoals.pending, (state: GoalsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getGoals.fulfilled, (state: GoalsState, action: any) => {
      state.loading = false;
      const data = action.payload;
      const { tp_report_goals } = action.meta.arg;
      const behaviorReductionGoals: ITreatmentPlanGoal[] = [],
        skillAcquisitionGoals: ITreatmentPlanGoal[] = [],
        caregiverGoals: ITreatmentPlanGoal[] = [];
      data.forEach((goal: ITreatmentPlanGoal) => {
        switch (goal.goal_type) {
          case GoalTypes.BehaviorReduction:
            behaviorReductionGoals.push({
              ...goal,
              targets: goal.targets
                ? goal.targets
                    .map((t) => ({ ...t, expanded: false }))
                    .sort(
                        (a: ITreatmentPlanGoalTarget, b: ITreatmentPlanGoalTarget) =>
                        a.order - b.order,
                    )
                : [],
              expanded: false,
            });
            break;
          case GoalTypes.SkillAcquisition:
            skillAcquisitionGoals.push({
              ...goal,
              targets: goal.targets
                ? goal.targets
                    .map((t) => ({ ...t, expanded: false }))
                    .sort(
                        (a: ITreatmentPlanGoalTarget, b: ITreatmentPlanGoalTarget) =>
                        a.order - b.order,
                    )
                : [],
              expanded: false,
            });
            break;
          case GoalTypes.Caregiver:
            caregiverGoals.push({
              ...goal,
              targets: goal.targets
                ? goal.targets
                    .map((t) => ({ ...t, expanded: false }))
                    .sort(
                        (a: ITreatmentPlanGoalTarget, b: ITreatmentPlanGoalTarget) =>
                        a.order - b.order,
                    )
                : [],
              expanded: false,
            });
            break;
          default:
            break;
        }
      });

      if (tp_report_goals) {
        state.tpReportGoalsList = data; // Update the tpReportList state
      } else {
        state.list = data; // Update the list state
        state.behaviorReductionGoals = behaviorReductionGoals;
        state.skillAcquisitionGoals = skillAcquisitionGoals;
        state.caregiverGoals = caregiverGoals;
      }

      state.validationErrors = {};
    })
    .addCase(getGoals.rejected, (state: GoalsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // migrateTreatmentPlanResourceVersion()
    .addCase(migrateTreatmentPlanResourceVersion.pending, (state: GoalsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(migrateTreatmentPlanResourceVersion.fulfilled, (state: GoalsState, action: any) => {
      const { preview } = action.meta.arg;
      state.loading = false;
      state.error = null;
      if (!preview) {
        toast.success('Goal converted successfully');
      }
    })
    .addCase(migrateTreatmentPlanResourceVersion.rejected, (state: GoalsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // createGoal()
    .addCase(createGoal.pending, (state: GoalsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(createGoal.fulfilled, (state: GoalsState, action: any) => {
      state.loading = false;
      state.list = [...state.list, action.payload];
    })
    .addCase(createGoal.rejected, (state: GoalsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // createHypothesizedFunctionRecord()
    .addCase(createHypothesizedFunctionRecord.pending, (state: GoalsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(createHypothesizedFunctionRecord.fulfilled, (state: GoalsState, action: any) => {
      const { goalId } = action.meta.arg;
      state.loading = false;
      state.behaviorReductionGoals = state.behaviorReductionGoals.map((goal) => {
        if (goal.id === goalId) {
          return {
            ...goal,
            hypothesized_functions: [...(goal.hypothesized_functions ?? []), action.payload],
          };
        }
        return goal;
      });
    })
    .addCase(createHypothesizedFunctionRecord.rejected, (state: GoalsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // updateHypothesizedFunctionRecord()
    .addCase(updateHypothesizedFunctionRecord.pending, (state: GoalsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateHypothesizedFunctionRecord.fulfilled, (state: GoalsState, action: any) => {
      const { goalId } = action.meta.arg;
      state.loading = false;
      state.behaviorReductionGoals = state.behaviorReductionGoals.map((goal) => {
        if (goal.id === goalId) {
          return {
            ...goal,
            hypothesized_functions: (goal.hypothesized_functions ?? []).map((hf, index) => {
              // We only allow to update the last record
              if (index === (goal.hypothesized_functions ?? []).length - 1) {
                return action.payload;
              }
              return hf;
            }),
          };
        }
        return goal;
      });
    })
    .addCase(updateHypothesizedFunctionRecord.rejected, (state: GoalsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // deleteHypothesizedFunctionRecord()
    .addCase(deleteHypothesizedFunctionRecord.pending, (state: GoalsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(deleteHypothesizedFunctionRecord.fulfilled, (state: GoalsState, action: any) => {
      const { goalId } = action.meta.arg;
      state.loading = false;
      state.behaviorReductionGoals = state.behaviorReductionGoals.map((goal) => {
        if (goal.id === goalId) {
          return {
            ...goal,
            hypothesized_functions: (goal.hypothesized_functions ?? []).filter((_, index) => {
              // We only allow last record to be delete
              return index !== (goal.hypothesized_functions ?? []).length - 1;
            }),
          };
        }
        return goal;
      });
    })
    .addCase(deleteHypothesizedFunctionRecord.rejected, (state: GoalsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // updateGoal()
    .addCase(updateGoal.pending, (state: GoalsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      updateGoal.fulfilled,
      (state: GoalsState, _action: PayloadAction<ITreatmentPlanGoal>) => {
        state.loading = false;
      },
    )
    .addCase(updateGoal.rejected, (state: GoalsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // removeGoal()
    .addCase(removeGoal.pending, (state: GoalsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(removeGoal.fulfilled, (state: GoalsState, action: any) => {
      state.loading = false;
      state.list = state.list.filter((goal) => goal.id !== action.meta.arg.goalId);
    })
    .addCase(removeGoal.rejected, (state: GoalsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // createGoalTarget()
    .addCase(createGoalTarget.pending, (state: GoalsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(createGoalTarget.fulfilled, (state: GoalsState, action: any) => {
      state.loading = false;
      state.list = state.list.map((goal) =>
        goal.id === action.meta.arg.goalId
          ? {
              ...goal,
              targets: goal.targets ? [...goal.targets, action.payload] : [action.payload],
            }
          : goal,
      );
    })
    .addCase(createGoalTarget.rejected, (state: GoalsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // updateGoalTarget()
    .addCase(updateGoalTarget.pending, (state: GoalsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateGoalTarget.fulfilled, (state: GoalsState, _action: any) => {
      state.loading = false;
    })
    .addCase(updateGoalTarget.rejected, (state: GoalsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // removeGoalTarget()
    .addCase(removeGoalTarget.pending, (state: GoalsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(removeGoalTarget.fulfilled, (state: GoalsState, action: any) => {
      state.loading = false;
      state.list = state.list.map((goal) =>
        goal.id === action.meta.arg.goalId
          ? {
              ...goal,
              targets: goal.targets?.filter((target) => target.id === action.meta.arg.targetId),
            }
          : goal,
      );
    })
    .addCase(removeGoalTarget.rejected, (state: GoalsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // activateGoal()
    .addCase(activateGoal.pending, (state: GoalsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(activateGoal.fulfilled, (state: GoalsState, _action: any) => {
      state.loading = false;
    })
    .addCase(activateGoal.rejected, (state: GoalsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // deactivateGoal()
    .addCase(deactivateGoal.pending, (state: GoalsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(deactivateGoal.fulfilled, (state: GoalsState, _action: any) => {
      state.loading = false;
    })
    .addCase(deactivateGoal.rejected, (state: GoalsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // activateTarget()
    .addCase(activateTarget.pending, (state: GoalsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(activateTarget.fulfilled, (state: GoalsState, _action: any) => {
      state.loading = false;
    })
    .addCase(activateTarget.rejected, (state: GoalsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // deactivateTarget()
    .addCase(deactivateTarget.pending, (state: GoalsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(deactivateTarget.fulfilled, (state: GoalsState, _action: any) => {
      state.loading = false;
    })
    .addCase(deactivateTarget.rejected, (state: GoalsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // changeTargetStage()
    .addCase(changeTargetStage.pending, (state: GoalsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(changeTargetStage.fulfilled, (state: GoalsState, _action: any) => {
      state.loading = false;
      state.error = null;
      toast.success('Target stage changed successfully');
    })
    .addCase(changeTargetStage.rejected, (state: GoalsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // updateTargetStage()
    .addCase(updateTargetStage.pending, (state: GoalsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateTargetStage.fulfilled, (state: GoalsState, _action: any) => {
      state.loading = false;
      toast.success('Target stage updated successfully');
    })
    .addCase(updateTargetStage.rejected, (state: GoalsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // deleteTargetStage()
    .addCase(deleteTargetStage.pending, (state: GoalsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(deleteTargetStage.fulfilled, (state: GoalsState, _action: any) => {
      state.loading = false;
      toast.success('Target stage deleted successfully');
    })
    .addCase(deleteTargetStage.rejected, (state: GoalsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // bulkStageChange()
    .addCase(bulkStageChange.pending, (state: GoalsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(bulkStageChange.fulfilled, (state: GoalsState, _action: any) => {
      state.loading = false;
      toast.success('Target stages changed successfully');
    })
    .addCase(bulkStageChange.rejected, (state: GoalsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    });
}

export const goalActions = { ...slice.actions, ...extraActions };
export const goalReducer = slice.reducer;
